import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import hinchada from "../../../images/hinchada.png";

const SWIPE_THRESHOLD = 60;
const SWIPE_VELOCITY_THRESHOLD = 450;

function RecordTable({ title, players, valueAccessor, valueFormatter = (value) => value }) {
    const topPlayers = (players || []).slice(0, 5);

    return (
        <div>
            <h4 className="text-white text-xl mb-3 border-b border-white/5 pb-2 text-center">{title}</h4>
            <ul className="space-y-2">
                {topPlayers.map((jugador, index) => (
                    <li
                        key={`${jugador.nombre}-${index}`}
                        className={`flex items-center justify-between ${index === 0 ? "text-white" : "text-white/80"}`}
                    >
                        <span className="flex items-center gap-3">
                            <span className={`text-xs font-bold ${index === 0 ? "text-white/40" : "text-white/30"}`}>
                                {index + 1}
                            </span>
                            <a href={`jugador/${jugador.nombre}`} className="hover:text-white hover:underline">
                                {jugador.nombreAbrev}
                            </a>
                        </span>
                        <span className="font-bold text-lg">{valueFormatter(valueAccessor(jugador))}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function HomeRecordsSection({
    partidosJugador,
    ptsJugador,
    rtJugador,
    astJugador,
    minPromJugador,
    ptsPromJugador,
    rtPromJugador,
    astPromJugador,
}) {
    const [mode, setMode] = useState("totales");
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setCurrentIndex(0);
    };

    const totalColumns = useMemo(
        () => [
            {
                title: "Partidos",
                players: partidosJugador,
                valueAccessor: (jugador) => jugador.carrera.partidos,
                valueFormatter: (value) => value,
            },
            {
                title: "Puntos",
                players: ptsJugador,
                valueAccessor: (jugador) => jugador.carrera.pts,
                valueFormatter: (value) => value,
            },
            {
                title: "Rebotes",
                players: rtJugador,
                valueAccessor: (jugador) => jugador.carrera.rt,
                valueFormatter: (value) => value,
            },
            {
                title: "Asistencias",
                players: astJugador,
                valueAccessor: (jugador) => jugador.carrera.ast,
                valueFormatter: (value) => value,
            },
        ],
        [partidosJugador, ptsJugador, rtJugador, astJugador]
    );

    const averageColumns = useMemo(
        () => [
            {
                title: "Minutos",
                players: minPromJugador,
                valueAccessor: (jugador) => jugador.carreraProm.mpg,
                valueFormatter: (value) => Number(value).toFixed(1),
            },
            {
                title: "Puntos",
                players: ptsPromJugador,
                valueAccessor: (jugador) => jugador.carreraProm.ppg,
                valueFormatter: (value) => Number(value).toFixed(1),
            },
            {
                title: "Rebotes",
                players: rtPromJugador,
                valueAccessor: (jugador) => jugador.carreraProm.rtpg,
                valueFormatter: (value) => Number(value).toFixed(1),
            },
            {
                title: "Asistencias",
                players: astPromJugador,
                valueAccessor: (jugador) => jugador.carreraProm.apg,
                valueFormatter: (value) => Number(value).toFixed(1),
            },
        ],
        [minPromJugador, ptsPromJugador, rtPromJugador, astPromJugador]
    );

    const columns = mode === "totales" ? totalColumns : averageColumns;
    const total = columns.length;

    const goNext = () => setCurrentIndex((i) => (i + 1) % total);
    const goPrev = () => setCurrentIndex((i) => (i - 1 + total) % total);

    const handleDragEnd = (_event, info) => {
        const movedRight =
            info.offset.x > SWIPE_THRESHOLD || info.velocity.x > SWIPE_VELOCITY_THRESHOLD;
        const movedLeft =
            info.offset.x < -SWIPE_THRESHOLD || info.velocity.x < -SWIPE_VELOCITY_THRESHOLD;

        if (movedRight) {
            goPrev();
            return;
        }

        if (movedLeft) {
            goNext();
        }
    };

    const current = columns[currentIndex];

    return (
        <section className="py-12 sm:py-24 relative overflow-hidden mt-16 -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-emerald-900 to-red-800 opacity-90"></div>
            <img
                alt="Hinchas de Aguada"
                className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-30"
                src={hinchada}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl text-white mb-2 uppercase tracking-tight font-bold">Récords históricos</h2>
                    <p className="text-slate-100 font-light text-lg">Estadísticas acumuladas desde la temporada 2009</p>
                </div>

                <div className="relative mx-3 overflow-hidden rounded-3xl border border-white/20 bg-white/5 p-5 shadow-2xl backdrop-blur-xl sm:mx-0 sm:p-8 md:p-12">
                    <span className="pointer-events-none absolute left-0 top-0 h-0.5 w-1/2 bg-green-400"></span>
                    <span className="pointer-events-none absolute bottom-0 right-0 h-0.5 w-1/3 bg-red-500"></span>
                    {/* Mode toggle */}
                    <div className="mb-6 flex justify-center border-b border-white/10 pb-4">
                        <div className="inline-flex p-1 rounded-xl border border-white/20 bg-[#0f1f15]/70 shadow-2xl">
                        <button
                            className={`relative z-10 px-4 md:px-6 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wide rounded-lg transition-colors duration-200 ${mode === "totales" ? "text-[#062a1a]" : "text-white/60 hover:text-white"}`}
                            onClick={() => handleModeChange("totales")}
                        >
                            {mode === "totales" ? (
                                <motion.span
                                    layoutId="home-records-mode-toggle-pill"
                                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                    className="absolute inset-0 -z-10 rounded-lg bg-white"
                                />
                            ) : null}
                            TOTALES
                        </button>
                        <button
                            className={`relative z-10 px-4 md:px-6 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wide rounded-lg transition-colors duration-200 ${mode === "promedios" ? "text-[#062a1a]" : "text-white/60 hover:text-white"}`}
                            onClick={() => handleModeChange("promedios")}
                        >
                            {mode === "promedios" ? (
                                <motion.span
                                    layoutId="home-records-mode-toggle-pill"
                                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                    className="absolute inset-0 -z-10 rounded-lg bg-white"
                                />
                            ) : null}
                            PROMEDIOS
                        </button>
                        </div>
                    </div>

                    {/* Carousel — mobile only */}
                    <div className="sm:hidden">
                        <div className="flex items-center gap-4">
                            {/* Left arrow */}
                            <button
                                onClick={goPrev}
                                className="p-2 hover:bg-white/15 rounded-full transition-colors text-white border border-white/10 flex-shrink-0 text-2xl leading-none"
                                aria-label="Anterior"
                            >
                                ‹
                            </button>

                            {/* Card */}
                            <motion.div
                                className="flex-1 cursor-grab active:cursor-grabbing select-none min-h-[180px]"
                                drag="x"
                                dragConstraints={{ left: 0, right: 0 }}
                                dragElastic={0.22}
                                dragMomentum={true}
                                dragTransition={{ bounceStiffness: 420, bounceDamping: 28 }}
                                onDragEnd={handleDragEnd}
                                whileDrag={{ scale: 0.985, cursor: "grabbing" }}
                                style={{ touchAction: "pan-y", willChange: "transform" }}
                            >
                                <RecordTable
                                    key={`${mode}-${currentIndex}`}
                                    title={current.title}
                                    players={current.players}
                                    valueAccessor={current.valueAccessor}
                                    valueFormatter={current.valueFormatter}
                                />
                            </motion.div>

                            {/* Right arrow */}
                            <button
                                onClick={goNext}
                                className="p-2 hover:bg-white/15 rounded-full transition-colors text-white border border-white/10 flex-shrink-0 text-2xl leading-none"
                                aria-label="Siguiente"
                            >
                                ›
                            </button>
                        </div>

                        {/* Dot indicators */}
                        <div className="flex justify-center gap-2 mt-6">
                            {columns.map((col, i) => (
                                <button
                                    key={col.title}
                                    onClick={() => setCurrentIndex(i)}
                                    className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? "bg-white scale-125" : "bg-white/30 hover:bg-white/50"}`}
                                    aria-label={col.title}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Grid — sm and above */}
                    <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
                        {columns.map((column) => (
                            <RecordTable
                                key={column.title}
                                title={column.title}
                                players={column.players}
                                valueAccessor={column.valueAccessor}
                                valueFormatter={column.valueFormatter}
                            />
                        ))}
                    </div>

                    <div className="mt-10 sm:mt-16 text-center">
                        <a href="records" className="group relative inline-flex items-center justify-between overflow-hidden rounded-xl border border-white/30 bg-white/5 py-1.5 pl-5 text-sm font-black uppercase tracking-wide text-white transition-all duration-300 hover:border-white/60 hover:bg-white/10 sm:pl-7 sm:text-base">
                            <span className="absolute left-0 top-0 h-0.5 w-1/2 bg-green-400"></span>
                            <span className="absolute bottom-0 right-0 h-0.5 w-1/3 bg-red-500"></span>
                            <span>Ver más récords y máximos</span>
                            <span className="ml-6 flex h-10 w-11 items-center justify-center text-2xl text-green-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-red-400 sm:ml-8 sm:h-11" aria-hidden="true">&#8594;</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default HomeRecordsSection;
